import { useToast } from '@chakra-ui/react';
import {
  compositionGroupApi,
  CopyConfig,
} from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { compositionGroupEvents } from '@texas/components/shared/compositionGroup/events';
import { useSetDimensionValueEvent } from '@texas/components/shared/compositionGroup/useSetDimensionValueEvent';
import { reactEvents } from '@bridge/reactEvents';

interface Props {
  variantId: number;
}

interface CompositionLocationData {
  variantId: number;
  supplierId?: number;
  branchId?: number;
  cellX?: number;
  cellY?: number;
}

export function useOptionsOverview({ variantId }: Props) {
  const {
    refetch,
    data: versions,
    loading: versionsLoading,
    set: setOptions,
  } = useApiResource(compositionGroupApi.optionsOverview);
  const toast = useToast();
  const { t } = useTranslation();
  const locationData = useRef<CompositionLocationData>({
    variantId,
  });

  useSetDimensionValueEvent(setOptions);

  useEffect(() => {
    refetch({
      variantId,
    });
  }, [refetch, variantId]);

  useEffect(() => {
    locationData.current = {
      variantId,
    };
  }, [variantId]);

  const refetchOptions = useCallback(() => {
    refetch({
      variantId,
    });
  }, [refetch, variantId]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    // We do no refetch here since dimensions are changed using auto update which would result in a lot of calls
    return compositionEvents.onDimensionValueChanged.sub(({ groupId }) => {
      setOptions((x) =>
        x
          ? x.map((o) => {
              if (o.id === groupId) {
                return { ...o, isReady: false };
              }

              return o;
            })
          : [],
      );
    });
  }, [setOptions]);

  useEffect(() => {
    return compositionEvents.onDimensionGroupSet.sub(() => refetchOptions());
  }, [refetchOptions]);

  useEffect(() => {
    return compositionGroupEvents.onArtworkChanged.subscribe(
      (updatedVariantId) => {
        if (updatedVariantId === variantId) refetchOptions();
      },
    );
  }, [refetchOptions, variantId]);

  useSignalSubscriptionEvents({
    triggerOnEvent: refetchOptions,
    signalEvents: [
      compositionGroupEvents.onArchived,
      compositionGroupEvents.onRestored,
      compositionGroupEvents.onCreate,
      compositionGroupEvents.onCopy,
      compositionEvents.fileUpdated,
      compositionEvents.onRename,
      compositionGroupEvents.onRename,
      compositionEvents.onMaterialAdded,
      compositionEvents.onMaterialChanged,
      compositionEvents.onMaterialRemoved,
      compositionEvents.onAdd,
      compositionEvents.onArchived,
      compositionEvents.onRestored,
      compositionGroupEvents.onLayoutChanged,
      compositionGroupEvents.onMarkReady,
      compositionGroupEvents.onMarkNotReady,
    ],
  });

  const createGroupRequest = async () => {
    setIsCreating(true);
    await request(
      compositionGroupApi.addGroup,
      [{ variantId }],
      (_) => {
        compositionGroupEvents.onCreate.dispatch();
        reactEvents.newOptionAddedWithVariant.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
    setIsCreating(false);
  };
  const copyGroup = async (props: {
    fromId: number;
    location?: CompositionLocationData;
    basedOnId?: number;
    copyConfig?: CopyConfig;
  }) => {
    setIsCreating(true);
    await request(
      compositionGroupApi.copyGroup,
      [
        props.fromId,
        {
          ...(props.location ?? locationData.current),
          replaceCompositionGroupId: undefined,
          basedOnId: props.basedOnId,
          copyConfig: props.copyConfig,
        },
      ],
      (_) => {
        compositionGroupEvents.onCopy.dispatch();
        if (!props.basedOnId) {
          reactEvents.newOptionAddedWithVariant.dispatch();
        }
        toast({
          title: t('general.copied'),
          status: 'success',
          isClosable: true,
        });
      },
      (error: ServerError) => {
        toast({
          title: t('general.copyFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
    setIsCreating(false);
  };
  const archiveGroupRequest = async (id: number) => {
    await request(
      compositionGroupApi.archive,
      [id],
      (_) => {
        compositionGroupEvents.onArchived.dispatch();
        reactEvents.variantArchived.dispatch();
      },
      (error: ServerError) => {
        toast({
          title: t('general.archiveFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  const mainProductApproval = (id: number) => {
    return versions?.find((v) => v.id === id)?.mainProductApproval;
  };

  return {
    versions,
    versionsLoading,
    createGroupRequest,
    copyGroup,
    mainProductApproval,
    locationData,
    archiveGroupRequest,
    refetchOptions,
    isCreating,
  };
}
